import { DestroyRef, Inject, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, combineLatest, from, of, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';
import { SigninRedirectArgs, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

import { ApplicationType } from '@kros-sk/models';
import { AppInsightsBaseService } from '@kros-sk/core/application-insights';

import { AuthDispatchersService } from '../../store/auth-dispatchers.service';
import { KROS_AUTH_IS_MOBILE } from '../../injection-tokens';
import { getUserRolePrefix, getUserRolePrefixes } from '../../utils-fn/auth.utils';
import { KROS_IDENTITY_SERVER_CONFIG, KrosIdentityServerConfig } from './kros-identity-server.config';
import { KrosUser } from './kros-user.model';

@Injectable()
export class KrosAuthService {
  readonly #isMobile = inject(KROS_AUTH_IS_MOBILE, { optional: true });

  manager: UserManager = null;

  private oidcUser$ = new BehaviorSubject<User | null>(null);
  private tokenSilentlyRenewed$ = new BehaviorSubject<void>(undefined);
  private clientId: string;
  private scope: string;
  private applicationType: ApplicationType;
  #signinRedirectInFlight: Promise<void> | null = null;

  private timer = timer(5000, 5000);
  private timerSub: Subscription;
  private timerRunning = false;
  private renewAttempts = 0;
  private maxRenewAttempts = 5;

  #signinSilentInFlight: Promise<User | null> | null = null;
  #silentRenewTokenInFlight: Promise<boolean> | null = null;
  #logoutInFlight: Promise<null> | null = null;

  constructor(
    @Inject(KROS_IDENTITY_SERVER_CONFIG) private identityServerConfig: KrosIdentityServerConfig,
    private authDispatchers: AuthDispatchersService,
    private logger: AppInsightsBaseService,
    private http: HttpClient,
    private destroyRef: DestroyRef
  ) {
    if ((this.identityServerConfig.deviceRegisterUrl || this.identityServerConfig.deviceUpdateUrl) && !this.#isMobile) {
      console.warn(
        'KrosAuthService: deviceRegisterUrl/deviceUpdateUrl is configured but KROS_AUTH_IS_MOBILE is not provided - ' +
          'devices will register as non-mobile. Provide it (see provideKrosAuthIsMobile).'
      );
    }

    // #signinRedirectInFlight intentionally stays set after a successful signinRedirect() - the page
    // is about to unload and any late startAuthentication() call (e.g. from the auth interceptor of
    // a request that resolves in the meantime) must not start a second redirect with a different state.
    // However, if the user returns from the identity server via the browser Back button, the page may be
    // restored from the back/forward cache (bfcache) with its whole JS state intact, including the lock.
    // Without this reset, every subsequent startAuthentication() would return the stale resolved promise
    // and no login redirect would ever start again in this page session. 'pageshow' with
    // event.persisted === true fires exactly and only on a bfcache restore.
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        this.#signinRedirectInFlight = null;
      }
    });
  }

  authorizationHeaderValue$ = combineLatest([this.oidcUser$, this.tokenSilentlyRenewed$]).pipe(
    switchMap(() => from(this.authorizationHeaderValue))
  );

  /**
   * User loader for APP_INITIALIZER token.
   *
   * Loading at app initialization time prevents problem with
   * promises, as UserManager.getUser() return promise object. That's the reason
   * why isLoggedIn would ALWAYS trigger redirect to auth authority, because at
   * the time when AuthGuards are calling isLoggedIn, this.user object is still null,
   * even when user is still signed in.
   */
  async initialize(clientId: string, scope: string, applicationType: ApplicationType): Promise<void> {
    this.clientId = clientId;
    this.scope = scope;
    this.applicationType = applicationType;
    this.oidcUser$
      .pipe(
        tap(() => this.stopRenewTokenTimer()),
        map((user) => ({ krosUser: this.mapOidcUserToUser(user), user })),
        distinctUntilChanged((previous, current) => this.usersAreSame(previous.krosUser, current.krosUser)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        if (this.isUserValid(data.user)) {
          return this.authDispatchers.setUser(data.krosUser);
        }
        this.authDispatchers.setEmptyUser();
      });

    this.manager = new UserManager(this.clientSettings);

    this.manager.events.addUserLoaded((user) => {
      this.sendOidcUser(user, this.identityServerConfig.deviceRegisterUrl, 'post');
    });

    this.manager.events.addSilentRenewError((e) => {
      this.#trackRenewFailure(e, 'Silent refresh unexpectedly canceled.');
      console.warn('silent renew failed', e);

      switch (e.message) {
        // handle hopeless
        case 'interaction_required':
        case 'login_required':
          this.removeOldUser();
          return;
        default:
          this.manager.stopSilentRenew();
          this.startRenewTokenTimer();
      }
    });

    this.manager.events.addUserSignedOut(() => {
      if (this.#isOptionalAuthentication) {
        void this.removeOldUser();
        return;
      }
      // redirect to login page from all tabs opened with same user account
      this.saveCurrentUrl(null, true);
      this.manager.signinRedirect();
    });

    let userFromManager = await this.manager.getUser();
    if (userFromManager) {
      try {
        // check if user is valid to login (user has been logged out or stored user is different)
        const status = await this.manager.querySessionStatus();
        if (status.sub === userFromManager.profile.sub) {
          if (userFromManager.expired) {
            // User is loaded but token is expired. Try to silent signIn
            userFromManager = await this.#signinSilent();
          }
        } else {
          this.saveCurrentUrl(null, true);
          await this.removeOldUser(false);
        }
      } catch (err) {
        // if user is not valid for login, remove user from user storage and redirect to login page
        this.#trackRenewFailure(err, 'User has been logged out.');
        console.log('user has been logged out');
        await this.removeOldUser();
      }
    }
    // sets user to store
    this.sendOidcUser(userFromManager, this.identityServerConfig.deviceUpdateUrl, 'put');
  }

  private sendOidcUser(user: User, url: string, method: 'post' | 'put' = 'post'): void {
    if (url && user) {
      this.http[method](url, { isMobile: this.#isMobile?.() ?? false })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error) => {
            console.error('Failed to send OIDC user:', error);
            this.oidcUser$.next(user);
            return of(null);
          })
        )
        .subscribe(() => {
          this.oidcUser$.next(user);
        });
    } else {
      this.oidcUser$.next(user);
    }
  }

  private isUserValid(user: User): boolean {
    return !!user && !user?.expired;
  }

  private removeOldUser(canRemoveRedirectUrl = true): Promise<null> {
    if (!this.#logoutInFlight) {
      const dropUser = this.#isOptionalAuthentication ? this.#removeUserAndStay() : this.#removeUserAndRedirect(canRemoveRedirectUrl);

      this.#logoutInFlight = dropUser.finally(() => {
        this.#logoutInFlight = null;
      });
    }
    return this.#logoutInFlight;
  }

  get authorizationHeaderValue(): Promise<string> {
    return this.getCurrentUser().then((user) => (this.isUserValid(user) ? `${user.token_type} ${user.access_token}` : ''));
  }

  get applicationTypeHeaderValue(): ApplicationType {
    return this.applicationType;
  }

  get applicationTypePrefix(): string {
    return getUserRolePrefix(this.applicationType);
  }

  get applicationTypePrefixes(): string[] {
    return getUserRolePrefixes(this.applicationType);
  }

  get token(): Promise<string> {
    return this.getCurrentUser().then((user) => (this.isUserValid(user) ? user.access_token : ''));
  }

  private getCurrentUser(): Promise<User | null> {
    return this.manager
      .getUser()
      .then((user) => (this.isUserValid(user) ? user : this.#signinSilent()))
      .catch((error) => {
        if (!this.#logoutInFlight) {
          this.#trackRenewFailure(error, 'User has been logged out while attempting to retrieve token.');
        }
        return this.removeOldUser();
      });
  }

  registerDevice(url?: string): void {
    this.getCurrentUser()
      .then((user) => {
        if (user) {
          this.http
            .post(this.identityServerConfig.deviceRegisterUrl, {
              isMobile: this.#isMobile?.() ?? false,
            })
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.startAuthentication(url);
              },
              error: (error) => {
                console.error('Failed to register device:', error);
              },
            });
        }
      })
      .catch((error) => {
        console.error('Failed to get current user:', error);
      });
  }

  startAuthentication(url?: string): Promise<void> {
    if (this.#signinRedirectInFlight) {
      return this.#signinRedirectInFlight;
    }

    this.saveCurrentUrl(url);
    this.#signinRedirectInFlight = this.manager.signinRedirect(this.getSignInRedirectArgs()).catch((error) => {
      this.#signinRedirectInFlight = null;
      throw error;
    });

    return this.#signinRedirectInFlight;
  }

  logOut(): Promise<void> {
    return this.manager.signoutRedirect();
  }

  /**
   * Adopts an existing identity-server session without ever redirecting to the login page.
   * Resolves `false` when there is none — the expected anonymous outcome, not an error.
   */
  async trySilentLogin(): Promise<boolean> {
    try {
      return this.isUserValid(await this.#signinSilent());
    } catch (error) {
      this.#trackRenewFailure(error, 'Silent login failed.', this.#isFrameTimeout(error));
      if (!this.#isSessionExpiredError(error)) {
        console.warn('silent login failed', error);
      }
      return false;
    }
  }

  async ensureValidToken(): Promise<boolean> {
    return this.manager.getUser().then((user) => {
      if (!!user) {
        if (user.expired) {
          return this.silentRenewToken();
        }
        return true;
      }
      return false;
    });
  }

  private silentRenewToken(): Promise<boolean> {
    if (!this.#silentRenewTokenInFlight) {
      this.#silentRenewTokenInFlight = this.#renewTokenAndRestartSilentRenew().finally(() => {
        this.#silentRenewTokenInFlight = null;
      });
    }
    return this.#silentRenewTokenInFlight;
  }

  private startRenewTokenTimer(): void {
    if (this.timerRunning) {
      return;
    }
    this.renewAttempts = 0;
    this.timerRunning = true;

    this.timerSub = this.timer.pipe(exhaustMap(() => from(this.silentRenewToken()).pipe(catchError(() => of(false))))).subscribe(() => {
      this.renewAttempts++;
      if (this.renewAttempts >= this.maxRenewAttempts) {
        this.stopRenewTokenTimer();
      }
    });
  }

  private stopRenewTokenTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerRunning = false;
  }

  private mapOidcUserToUser(oidcUser: User): KrosUser | null {
    if (oidcUser) {
      return {
        email: oidcUser.profile.email,
        name: oidcUser.profile.name,
        userId: +oidcUser.profile.sub,
        externalUserId: +oidcUser.profile['external_id'] || undefined,
      };
    }
    return null;
  }

  private usersAreSame(u1: KrosUser, u2: KrosUser): boolean {
    return JSON.stringify(u1) === JSON.stringify(u2);
  }

  private getSignInRedirectArgs(): SigninRedirectArgs {
    const urlRoute = this.getUrlRoute();

    if (this.isValidExternalLoginRoute(urlRoute)) {
      return { extraQueryParams: { externalLoginAuthority: urlRoute } };
    }

    return undefined;
  }

  private isValidExternalLoginRoute(urlRoute: string): boolean {
    const upperCaseUrlRoute = urlRoute.toUpperCase();
    return this.identityServerConfig.externalLoginRoutes?.some((path) => path.toUpperCase() === upperCaseUrlRoute);
  }

  private getUrlRoute(): string {
    const { pathname } = window.location;

    if (!pathname || !pathname.includes('/')) {
      return pathname || '';
    }

    const redirectUrlRouteParts = pathname.split('/');
    return redirectUrlRouteParts[redirectUrlRouteParts.length - 1];
  }

  /**
   * If user is not logged in, and current location is not '/',
   * then save it to local store for later use after login redirect
   */
  private saveCurrentUrl(url?: string, forceSave = false): void {
    // Bugfix #154614. LocalStorage could be null or throw exceptions in some cases (full storage, private mode, bug, ...).
    if (!localStorage) {
      this.logger?.trackException(new Error('Null localStorage on saveCurrentUrl'), 'KrosAuth', {
        message: 'Null localStorage on saveCurrentUrl',
        url,
      });
      return;
    }

    try {
      if (forceSave || !localStorage.getItem('redirectUrl')) {
        if (window.location.pathname.length > 1 || window.location.search.length > 1) {
          localStorage.setItem('redirectUrl', window.location.pathname.concat(window.location.search));
        }
        if (url) {
          localStorage.setItem('redirectUrl', url);
        }
      }
    } catch (error) {
      this.logger?.trackException(error, 'KrosAuth', { message: 'LocalStorage exception on saveCurrentUrl', url });
    }
  }

  /**
   * Create and return client settings object.
   * this.identityServerConfig is dependent on APP_INNITIALIZATION process.
   * APP_INNITIALIZATION is required to run before this service is loaded.
   */
  private get clientSettings(): UserManagerSettings {
    return {
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      authority: `${this.identityServerConfig.url}`,
      client_id: this.clientId,
      redirect_uri: `${this.identityServerConfig.clientUrl}/oidc/login-redirect`,
      extraQueryParams: this.identityServerConfig.applicationId ? { app_id: this.identityServerConfig.applicationId } : undefined,
      post_logout_redirect_uri: `${this.identityServerConfig.postLogoutUrl ?? this.identityServerConfig.clientUrl}`,
      response_type: 'code',
      scope: this.scope,
      filterProtocolClaims: true,
      loadUserInfo: false,
      automaticSilentRenew: true,
      silent_redirect_uri: `${this.identityServerConfig.clientUrl}/oidc/silent-refresh.html`,
      silentRequestTimeoutInSeconds: 20,
      checkSessionIntervalInSeconds: 30,
      // should resolve No matching state found in storage
      response_mode: 'query',
      monitorSession: true,
    };
  }

  #signinSilent(): Promise<User | null> {
    if (!this.#signinSilentInFlight) {
      this.#signinSilentInFlight = this.manager.signinSilent().finally(() => {
        this.#signinSilentInFlight = null;
      });
    }
    return this.#signinSilentInFlight;
  }

  async #renewTokenAndRestartSilentRenew(): Promise<boolean> {
    try {
      await this.#signinSilent();
      console.log('silent renew restarted');
      this.manager.startSilentRenew();
      if (this.logger) this.logger.trackEvent('KrosAuth', { message: 'Silent renew successful.' });
      this.tokenSilentlyRenewed$.next();
      return true;
    } catch (error) {
      if (!this.#logoutInFlight) {
        this.#trackRenewFailure(error, 'Silent renew failed.');
      }
      console.log('silent renew failed', error);
      await this.removeOldUser();
      return false;
    }
  }

  /** @see KrosIdentityServerConfig.optionalAuthentication */
  get #isOptionalAuthentication(): boolean {
    return this.identityServerConfig.optionalAuthentication === true;
  }

  /** Drops the user and publishes the anonymous state, keeping the visitor on the current page. */
  async #removeUserAndStay(): Promise<null> {
    await this.manager.removeUser();
    this.oidcUser$.next(null);
    return null;
  }

  async #removeUserAndRedirect(canRemoveRedirectUrl: boolean): Promise<null> {
    if (canRemoveRedirectUrl) {
      localStorage.removeItem('redirectUrl');
      this.saveCurrentUrl(null, true);
    }

    await this.manager.removeUser();
    await this.manager.signinRedirect();
    return null;
  }

  /**
   * @param expected Outcomes the caller knows are ordinary, reported as an event rather than an exception. The probe
   * that only asks whether a session exists passes it for a frame that timed out: a browser blocking a third-party
   * frame, or an identity server slower than the frame's own timeout, is a "no", not a fault. A renew that times out
   * on an established session is not expected and stays an exception.
   */
  #trackRenewFailure(error: unknown, message: string, expected = false): void {
    if (!this.logger) {
      return;
    }

    const normalizedError = error instanceof Error ? error : new Error(String(error));
    if (expected || this.#isSessionExpiredError(error)) {
      this.logger.trackEvent('KrosAuth', { message, error: normalizedError.message });
    } else {
      this.logger.trackException(normalizedError, 'KrosAuth', { message });
    }
  }

  /**
   * The silent-login frame did not answer in time. `oidc-client-ts` documents its `name` as the marker to detect the
   * class by, which also survives a bundle holding two copies of the library, where `instanceof` would not.
   *
   * The same timeout comes from a frame that a misconfigured `silent_redirect_uri` or a CSP never let answer, and the
   * library cannot tell that apart from a visitor who has no session. The probe runs for every anonymous visitor of a
   * public document, so an exception per timeout was the noise this replaces. A broken configuration still shows: in
   * the console, and as the timeout event's share of visits jumping from the usual few per cent to all of them.
   */
  #isFrameTimeout(error: unknown): boolean {
    return (error as { name?: unknown } | null)?.name === 'ErrorTimeout';
  }

  #isSessionExpiredError(error: unknown): boolean {
    const oidcError = error as { message?: string; error?: string };
    const errorCode = oidcError?.error ?? oidcError?.message;
    return errorCode === 'login_required' || errorCode === 'interaction_required';
  }
}
